/**
 * What this operator can actually reach.
 *
 * Document 17 requires the consoles' navigation to be permission-aware, and the
 * reason is arithmetic: 273 distinct scopes exist across the platform and no
 * operator holds more than a fraction of them. Navigation that lists every page
 * and lets the service refuse gives a dispatcher a sidebar of doors that mostly
 * do not open, and an operator learns within a shift to distrust the whole
 * thing — including the doors that would have opened.
 *
 * So a page declares what it costs, and the model answers three different
 * questions that a naive implementation collapses into one:
 *
 *   - visible   — show it in navigation
 *   - reachable — allow it to be opened
 *   - readOnly  — open it, but do not offer the commands
 *
 * The third is the one that matters most here and the one usually missed. A
 * dispatcher who can watch the exceptions board but cannot resolve an exception
 * still needs the board: they are the person who notices, escalates, and tells
 * somebody who can act. Hiding it because they lack the write scope removes the
 * watching as well as the acting.
 *
 * Nothing here talks to a service. Scopes arrive in the access token, which is
 * the only authority on what this actor may do, and this module decides only
 * what to draw. A frontend is never an authorization authority — apps/README.md
 * says so, and every command still goes to a service that checks again.
 */

export interface PageDefinition {
  readonly id: string;
  readonly title: string;
  readonly section: string;
  /**
   * The ways an operator can earn sight of this page: alternatives, each a set
   * of scopes all of which are needed.
   *
   * The shape is OpenAPI's own — an array of requirements read as "any of
   * these", the scopes within one read as "all of these" — because that is the
   * shape of the thing it has to agree with. It was a flat list of scopes, any
   * one of which sufficed, and that model cannot state what five published
   * operations actually require: DRV-05's availability listing needs
   * `driver-availability:read` **and** `driver-availability:workload` together,
   * because a listing returns rows the caller did not name and the
   * by-identifier read beside it cannot make that check row by row. Under the
   * flat list the page could only claim one or the other, so an operator
   * holding just the read saw the page in the sidebar and got a 403 on opening
   * it — a door that does not open, produced by the module whose whole purpose
   * is not drawing those.
   *
   * The example used to be the deliveries board, which is no longer either an
   * example or an owner of what it named: EXP-008 serves that board now and its
   * gate is one scope. `tests/service-codes.test.ts` is what caught the stale
   * code, by reading this file as text — which is the only way to catch a wrong
   * claim about a service, because a comment is where such a claim lives.
   */
  readonly readRequirements: readonly (readonly string[])[];
  /**
   * The ways an operator can earn the right to act on it, in the same shape. A
   * page with none is a page nobody acts on from here, not a page anybody may
   * act on.
   */
  readonly writeRequirements?: readonly (readonly string[])[];
  /**
   * A page the design team's catalogue does not list, because it was asked for after the bundle
   * was cut.
   *
   * It keeps such a page out of the completion meter's numerator and reports it beside the ratio
   * instead. The denominator is the count of `###` headings in `SCREEN_SPEC.md`, so a routed
   * page the catalogue has never heard of would add one to the top of the fraction and none to
   * the bottom — a figure that counts something its denominator cannot, which is the one failure
   * that meter must not have.
   *
   * Not a lesser page. The work is real; it is simply not progress against a catalogue that did
   * not ask for it. The tenant admin console's four analytics pages carry the same flag.
   */
  readonly commissioned?: true;
  /**
   * Why the page exists, in the operator's terms. Carried here because an
   * empty state and a forbidden state both have to say something true about a
   * page the operator cannot currently see into.
   */
  readonly purpose: string;
  /**
   * Why this page cannot be built yet, if it cannot.
   *
   * Pages in the screen inventory that the platform cannot support are
   * recorded here rather than quietly dropped: a page missing from this file
   * looks like an oversight, and a page with a reason attached is a piece of
   * work somebody can pick up. Blocked pages never appear in navigation and can
   * never be opened, so nothing renders against a permission that does not
   * exist.
   *
   * Two pages have left this list, and neither was fixed in the console.
   * manual-assignment was blocked by an operation that appeared in no contract,
   * and geofences by a service that had no read operation at all, so in both
   * cases publishing the operation was the fix.
   *
   * The list held nothing until 22 October, and that was itself the fault this field warns about:
   * `surge` was not blocked, it was **absent**, and a page the design draws with nothing recorded
   * against it is indistinguishable from a page nobody thought of. It is here now.
   */
  readonly blockedBy?: string;
  /**
   * The services this page talks to, by their canonical identifiers.
   *
   * Reads, for every page but one. The exceptions board reads EXP-008 and commands DEL-08 and
   * LOG-11, so it names three: `tests/page-routes.test.ts` requires every scope in
   * `writeRequirements` to command something on an owner, and an owner list naming only the
   * aggregate would have claimed two write permissions that command nothing — which is the enabled
   * button that test exists to prevent.
   *
   * Data rather than prose because prose cannot be checked and I got three of
   * these wrong in one sitting — copied from the screen inventory rather than
   * looked up. Two of the three named a service that exists and does something
   * else entirely, which is the kind of error that reads as correct forever.
   */
  readonly owners: readonly string[];
  /**
   * The Material Symbols Rounded glyph the rail draws for this page.
   *
   * Not decoration: between 640 and 1240 the design collapses the rail to 76px and the glyph is the
   * only thing left of a menu item, so a page without one is an unlabelled square. That is why this
   * field is required rather than optional — a new page cannot be added without deciding what it
   * looks like when the label is gone.
   *
   * Every value is read from `ICONS` in `02 Console Shell v3.dc.html`, which is the only place in
   * the bundle that states a glyph for this console's page list by name. `04 Dispatch Exceptions
   * Board` and `05 Dispatch Live Map` carry their own shorter lists and agree with it on every
   * label they share except one: they draw `hub` for Live map where the shell draws `map`. The
   * shell's reading is taken because `Console Shell/01 Shell light.jpg` renders a folded map there,
   * and because `hub` is that same file's icon for the Operations console as a whole. Recorded as
   * Q-06.
   *
   * `service-areas` appears in no design nav at all, so its glyph is the fallback the shell design
   * itself applies to a label its `ICONS` map does not carry — `ICONS[l]||'circle'`. Choosing a
   * pictogram for it would be inventing design content; `circle` is the design's own answer to not
   * having one. Recorded as Q-07.
   */
  readonly glyph: string;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * The dispatch console's pages, from the screen inventory.
 *
 * Every scope named here is asserted against the published contracts by the
 * suite, so a page cannot quietly ask for a permission no service grants — the
 * failure that would put an operator in front of a door with no key anywhere in
 * the platform.
 */
export const dispatchConsolePages: readonly PageDefinition[] = [
  {
    id: "live-map",
    glyph: "map",
    owners: ["EXP-008"],
    title: "Live map",
    section: "Now",
    purpose: "Everything happening in the operating area, at once",
    // One owner and one scope, and this field said two owners and four scopes while the page was
    // actually reading seven services under twelve. That was not a documentation slip: R-09 recorded
    // that correcting `owners` to the seven was refused by two tests at once, because
    // `tests/service-codes.test.ts` makes every owner enforce a scope the page asks for, and
    // widening `readRequirements` to the union of seven listings' scopes would have put the
    // flagship page in the sidebar of an operator holding only `service-area:read`. It was a
    // product decision about who sees this page, left recorded rather than made under one screen.
    //
    // The aggregate settles it rather than deciding it. The page reads one operation —
    // `GET /v1/tenants/{tenantId}/dispatch-console/live-map` — which enforces one scope, so the
    // owner and the requirement agree with the code for the first time and the question of how to
    // combine twelve no longer arises.
    //
    // `dispatch-console:read` is a gate and not a substitute: it grants the right to ask, and what
    // comes back is decided by each owning service against this operator's own forwarded token.
    // So an operator holding this and nothing else sees the page, and sees every layer of it report
    // `forbidden` with the scope it needs named — which is a better answer than a hidden page, and
    // the reason ADR 0020 forbids the aggregate holding a credential of its own.
    //
    // The twelve scopes the page used to need are still granted to this console: the driver roster,
    // the demand queue, the service areas page and the geofences page name eight of them between
    // them. The four that no other page names are the two driver-location scopes and the two
    // trip-tracking ones, which now leave this console's token request. R-14.
    readRequirements: [["dispatch-console:read"]],
  },
  {
    id: "demand-queue",
    glyph: "pending_actions",
    // The read model **and** the orchestrator, which it was not until manual assignment landed
    // here.
    //
    // DSP-05's projection is still the only thing on this platform that answers a list, and the
    // board is entirely its rows. DSP-04 joined because the page's own `Manual assign` asks it to
    // rank couriers and to send one an offer — and `owners` is what the deployment reads to decide
    // which paths this console's front door will proxy. Declaring the service the page calls is
    // therefore not bookkeeping: without it the form's first request is answered 405 by nginx,
    // before it reaches anything that could explain itself. Measured exactly that way.
    owners: ["DSP-05", "DSP-04"],
    title: "Demand queue",
    section: "Now",
    purpose: "Requests waiting for a driver, with the deadline each is racing",
    readRequirements: [["dispatch-operations:read"]],
    // Ranking couriers and sending one an offer are both writes, and both spend the scope that
    // already drives this saga: a dispatcher choosing by hand is driving it by hand.
    writeRequirements: [["dispatch:orchestrate"]],
  },
  {
    id: "exceptions",
    glyph: "report",
    owners: ["EXP-008", "DEL-08", "LOG-11"],
    title: "Exceptions",
    section: "Now",
    purpose: "Everything that has gone wrong and still needs a person",
    // The screen inventory calls this the page operators will live in, and it reads one operation:
    // `GET /v1/tenants/{tenantId}/dispatch-console/exceptions`, which merges three services'
    // exceptions into one urgency-ordered queue. R-05 measured why that merge cannot be done in a
    // browser — three separately keyset-paged sources, where page two of the merged list is page two
    // of none of them — and R-14 records the page moving onto it.
    //
    // Reading and acting are now genuinely different permissions, which is what makes the split
    // below true rather than a formality. The aggregate serves reads only: it answers what this
    // operator's own forwarded token was entitled to see, layer by layer, and says which layers it
    // was not. Every command on the detail panel goes to the service that owns the exception, so the
    // write scopes are still the owning services' and are still what decides whether this page is
    // view-only.
    //
    // Both write scopes stay granted to this console and both are still named here: a dispatcher who
    // may resolve an exception but may not list them opens the page and cannot browse it, which is
    // the actor the write-without-read branch in `resolvePage` exists for.
    readRequirements: [["dispatch-console:read"]],
    writeRequirements: [["delivery-exception:write"], ["logistics-exception:update"]],
  },
  {
    id: "active-rides",
    glyph: "local_taxi",
    owners: ["TRP-01"],
    title: "Active rides",
    section: "In flight",
    purpose: "Every trip currently running",
    // Both scopes in one requirement, because the route wants both and either alone is a refusal.
    //
    // TRP-01 now serves `GET /v1/tenants/{tenantId}/trips`, so this page has a route for the first
    // time. That route requires the operator scope in addition to the read scope, because a listing
    // returns rows the caller did not name — the by-identifier read beside it can refuse a caller
    // who is neither the rider nor the driver of the trip they asked for, and a listing cannot make
    // that check row by row.
    //
    // This page is why `readRequirements` is a list of sets rather than a list of scopes. As a flat
    // any-of list it could name only one of the two, and both choices were wrong: `trip:read` alone
    // would put the board in the sidebar of a rider-facing actor who would open it and get a 403,
    // and `trip:workload:read` alone — what it said until now — described a set of scopes the route
    // also refuses. One requirement of two says the true thing, and hides the page from anyone who
    // holds only half of it.
    readRequirements: [["trip:read", "trip:workload:read"]],
    /*
     * Cancelling a trip for the rider, which is the one trip-moving action the design draws.
     *
     * Its own note on this board: *"Cancelling a trip for the rider asks for a reason and shows the
     * fee before you confirm."* Not a lifecycle walk — no `Mark arrived` or `Start trip` appears
     * anywhere in the design — so this board commands exactly that one transition and no other.
     *
     * **`trip:workload:transition` was published by nothing until 21 October.** TRP-01's
     * `authorizeTransition` has always honoured it, and its own integration tests mint tokens
     * carrying it, but no contract named it — so no client could hold it and this board could not
     * call an operation the design cites. The scope published alongside it, `trip:driver:transition`,
     * is the one of the three that can authorize nothing: it requires the actor's identity subject
     * to equal the trip's `driverId`, and R-29 is about those never being the same thing.
     */
    writeRequirements: [["trip:workload:transition"]],
  },
  {
    id: "departures",
    /*
     * `departure_board` is the Material Symbols glyph for exactly this, and nothing else on this
     * console spends it. The shell's `ICONS` map does not name this page, as it does not name
     * Service areas (Q-07), Driver broadcasts (Q-239) or Surge and zones — so the glyph is simply
     * available.
     */
    glyph: "departure_board",
    owners: ["MOB-04"],
    title: "Departures",
    section: "In flight",
    purpose: "Every scheduled journey, how full it is, and what state it is in",
    /*
     * There is no design file for this screen and there could not be: scheduled departures were
     * asked for after the design bundle was cut, so `SCREEN_SPEC.md` catalogues 114 screens and
     * this is a 115th. `completion-meter-catalogue.test.ts` caught exactly that — a numerator
     * above its own denominator — which is the arithmetic the flag exists to prevent.
     */
    commissioned: true,
    /*
     * **This page is what deploys the seat-booking spine.**
     *
     * The deployment set is the closure of services a buildable console page cites, and until this
     * entry existed nothing cited MOB-04 — which ADR 0100 §6 calls shipping dark and which means
     * there was no container, no route and no credential for any of it. The entry is the mechanism,
     * not a flag.
     *
     * One scope, because the board makes one read's worth of requests: the departures listing and
     * the route listing both take `departure:read`.
     */
    readRequirements: [["departure:read"]],
    /*
     * Declared although the page's primary command is drawn and refused.
     *
     * The command is real — MOB-04 publishes `POST /v1/departures` under this scope — and what is
     * missing is the form, which needs a vehicle's seat layout from a service this console does not
     * read. Declaring the scope is what makes the button appear enabled-looking to an operator who
     * could use it once the form exists, and what keeps the channel grant honest in the meantime.
     * Q-250.
     */
    writeRequirements: [["departure:write"]],
  },
  {
    id: "active-deliveries",
    glyph: "local_shipping",
    /*
     * EXP-008, and one scope, for the third time on this console.
     *
     * This named the delivery-order service and the tracking projection with three of their scopes,
     * which was true while the board was going to read them from the browser. It does not: the
     * aggregate joins the in-flight orders to the tracking projection for the courier and the
     * roster for that courier's name, server-side, with a bounded sweep — because the tracking
     * projection publishes no listing at all and the `Courier` column would otherwise be an N+1
     * across a service boundary on a refreshing screen. R-20 is the measurement.
     *
     * Leaving the old gate would not have been a documentation slip, it would have been a page
     * nobody can open: the board asks for `dispatch-console:read`, so an operator holding exactly
     * the three scopes named before would have seen the page in the rail and been refused by every
     * request it made, while an operator holding the scope it actually uses would not have seen the
     * page at all. R-14 made this same correction for the live map and the exceptions board.
     *
     * Those three services are deliberately not named by code here. `tests/service-codes.test.ts`
     * reads this file as *text* and fails on any code that is not a declared owner, because two of
     * the three codes that were once wrong lived in comments — which is exactly where a stale claim
     * about a service goes to hide.
     */
    owners: ["EXP-008"],
    title: "Active deliveries",
    section: "In flight",
    purpose: "Every delivery currently running",
    readRequirements: [["dispatch-console:read"]],
  },
  {
    id: "offer-monitor",
    glyph: "campaign",
    owners: ["DSP-02"],
    title: "Offer monitor",
    section: "In flight",
    purpose: "Offers out, accepted, expired and declined",
    // DSP-02 now serves `GET /v1/ride-offers`, so this board has a route. Both scopes together,
    // because that route asks for each of them: a driver holds `ride-offer:read` and
    // `ride-offer:respond` and reaches their own offer through the by-identifier read, which can
    // check "is this yours" because it has the offer. A listing cannot.
    //
    // The board is ordered soonest-to-expire, unlike the other three, and the page should not
    // re-sort it: an offer monitor exists to show what is about to lapse unanswered.
    readRequirements: [["ride-offer:read", "ride-offer:workload"]],
  },
  {
    id: "driver-roster",
    glyph: "badge",
    owners: ["DRV-05", "DRV-01"],
    title: "Driver roster",
    section: "Supply",
    purpose: "Who is online, where they are, and what they are on",
    // Two owners and two halves, and both now serve a listing: DRV-05 answers
    // `GET /v1/driver-availability` and DRV-01 answers `GET /v1/driver-profiles`. Each is a pair of
    // scopes because each route asks for both — neither by-identifier read carries an ownership check,
    // so the read scope alone already reaches any driver in the tenant, and what a listing adds is
    // enumeration.
    //
    // The two lists are joinable now, and were not when this page was written. DRV-01 keyed its table on
    // `profile_id` and published the driver as `id` while its sibling services all used
    // `driverId`, so a roster putting names against availability rows was assuming a relationship no
    // service stated. It was one entity called two things, and Document 07 §14 had already settled the
    // name: DRV-01 keys on `driver_id` and publishes `driverId`, which
    // `tests/contract/driver-identifier.test.ts` holds for the whole domain.
    readRequirements: [
      ["driver-availability:read", "driver-availability:workload"],
      ["driver-profile:read", "driver-profile:workload"],
    ],
  },
  /*
   * `Manual assignment` is not a page, and the design never said it was.
   *
   * This console's navigation declared one and `main.tsx` routed twelve of thirteen, so it was the
   * single entry in the sidebar that opened `PageOutlook`. Q-259 asked the question plainly — *is
   * manual assignment a page, or the demand queue's own action?* — and both sources agree on the
   * answer. The design's own `NAV` has seven entries and this is not one of them; its `FORMS.queue`
   * is a modal on the demand queue whose toolbar button reads `Manual assign`.
   *
   * The services agree too. `POST /v1/ride-assignments` requires an offer that already exists, so
   * an operator cannot assign out of nothing — what they can do is send one courier an offer, which
   * is what the design's `Send offer` button does and what `ui/ManualAssign.tsx` now builds.
   *
   * The entry is gone rather than pointed at the queue: a sidebar item that opens a different page
   * than its name teaches people the sidebar is approximate. `?page=manual-assignment` still lands
   * on `PageOutlook`, which is the right answer for a stale bookmark.
   */
  {
    id: "broadcasts",
    /*
     * **`podcasts`, where this console's own design says `campaign`** — and the two design files
     * disagree rather than this being a deviation from one.
     *
     * `19 WP18 Dispatch Operations.dc.html` carries `['broadcasts','campaign','Driver
     * broadcasts']`. `02 Console Shell v3.dc.html` carries `ICONS={'Offer monitor':'campaign'}`
     * — the same glyph, a different page, and the offer monitor has been drawing it since that
     * board was built. The collapsed rail is 76px of glyph and nothing else, so both cannot have
     * it; `navigation.test.ts` pins that, and the conflict was invisible until this page existed.
     *
     * **Nothing that already exists is restyled to settle it.** The shell map names ten of this
     * console's eleven pages and does not name this one, which is the same case as Service areas
     * (Q-07): the design's own answer to a label its map does not carry is `ICONS[l]||'circle'`,
     * and `circle` is already Service areas'. So the fallback itself collides, and a glyph is
     * chosen here — `podcasts`, a Material Symbols Rounded broadcast tower, which is the nearest
     * unused glyph to the one the dispatch design asked for.
     *
     * **This is design content chosen by a build, which CLAUDE.md §1 forbids doing silently.**
     * Recorded as Q-239 with both files quoted, for the design team to settle — the answer is
     * one line either way, and until it comes the rail stays unambiguous.
     */
    glyph: "podcasts",
    owners: ["NTF-01"],
    title: "Driver broadcasts",
    section: "Supply",
    purpose: "Messages to drivers on shift, by zone or group",
    /*
     * **NTF-01, where the design cites the push adapter**, and that citation is the whole reason
     * this page did not exist. The push adapter holds one row per message *per device*, with a
     * vault reference for the device and a status that ends at `delivered` — its code is described
     * rather than spelled here, because this file names no service it does not read from and its
     * own test cannot tell a code in a comment from an owner. The
     * board draws `Message · Audience · Sent` over `Road closure on Waiyaki Way · Westlands · 64
     * drivers` — one message to many people, which no row on this platform was until 22 October.
     *
     * Publishing a listing of push attempts would have filled two of three columns with per-device
     * rows and left out the column that makes a broadcast a broadcast. So the subject was modelled
     * in the orchestrator instead, where an audience belongs.
     *
     * **The design's section is `Safety and supply` and this console's is `Supply`.** The rail
     * groups are this console's own and predate the broadcast board; putting it in the nearest
     * existing group keeps the rail as the operator already knows it, rather than adding a fifth
     * group for one page. Recorded rather than silently decided.
     */
    readRequirements: [["broadcast:read"]],
    /*
     * **The first write on this console that is actually offered.** Every other primary action
     * here is drawn and refused, because the design's form collects fields the operation does not
     * take — a phone booking is a ride request, a quote, a dispatch and then a trip, and no
     * operation starts that chain from an address.
     *
     * This one lines up exactly: the design's form is `Zones (chips) · Service (chips) · English
     * (area) · Swahili (area) · Send (select)`, and `POST /v1/broadcasts` takes an audience of
     * zones and service classes, a body by locale, and an optional time. Nothing is invented and
     * nothing designed is left out.
     */
    writeRequirements: [["broadcast:send"]],
  },
  {
    id: "surge",
    /*
     * `bolt`, from this console's own design — and unlike Q-239 there is nothing to settle here.
     *
     * `19 WP18 Dispatch Operations.dc.html` carries `['surge','bolt','Surge and zones','3']`. The
     * shell's `ICONS` map does not name this page, as it does not name Service areas (Q-07) or
     * Driver broadcasts (Q-239) — but `bolt` is spent by nothing, so the glyph the dispatch design
     * asks for is simply available. The shell's own fallback, `circle`, is Service areas'.
     */
    glyph: "bolt",
    /*
     * **The second owner is not the one `SCREEN_SPEC.md` names.**
     *
     * The spec cites the reverse-geocoding adapter, which turns a point into an address. This
     * board's `Zone` column is a list of service areas, and no amount of reverse geocoding
     * produces one — so the owner here is the service-area service instead.
     *
     * Looked up rather than copied, which is why this field exists at all: three owners were once
     * copied straight from the screen inventory and two of them named a service that does
     * something else entirely. `service-codes.test.ts` forbids spelling the code being corrected,
     * for the same reason — a comment naming a service reads as a service this page touches. The
     * code is in docs/design/UNBUILT_SCREENS.md, where the correction is recorded with its
     * evidence.
     */
    owners: ["DSP-06", "PRC-03", "GEO-06"],
    title: "Surge and zones",
    section: "Supply",
    purpose: "Live demand and supply by zone, and surge in effect",
    /*
     * **One set of three, not three alternatives.** `readRequirements` is any-of over sets and
     * all-of within one, and this board is three readings joined rather than three ways of getting
     * the same answer: the areas are the rows, the counts are the middle column, and the overrides
     * are the third. An operator holding one of the three would open a board that is mostly its
     * error state — which is what the first spelling of this field declared, and what
     * `navigation.test.ts` caught by asserting a narrowly-scoped operator sees one page.
     */
    readRequirements: [["service-area:read", "dispatch-operations:read", "dynamic-pricing:read"]],
    writeRequirements: [["dynamic-pricing:write"]],
  },
  {
    id: "service-areas",
    glyph: "circle",
    owners: ["GEO-06"],
    title: "Service areas",
    section: "Configuration",
    purpose: "See and edit where the platform operates",
    readRequirements: [["service-area:read"]],
    writeRequirements: [["service-area:write"]],
  },
  {
    id: "geofences",
    glyph: "polyline",
    owners: ["GEO-05"],
    title: "Geofences",
    section: "Configuration",
    purpose: "Draw and edit the geofences trips are evaluated against",
    // Unblocked by publishing GET /v1/tenants/{tenantId}/geofences/{geofenceId}
    // and its own read scope. The recorded blocker said GEO-05 had no read
    // scope; reading the service found it had no read *operation* either, so a
    // geofence could not be looked at by anybody holding any permission — and
    // `PUT` requires an `expectedVersion` that was disclosed only in a write
    // response, which made amending a fence impossible for anyone but whoever
    // last wrote one.
    readRequirements: [["geo:geofence:read"]],
    writeRequirements: [["geo:geofence:write"]],
  },
] as const;

/**
 * Whether the granted scopes satisfy any one of the alternatives outright.
 *
 * `every` inside `some`, and the `every` is the half that took a rewrite to get: a partly-held
 * requirement is not partial access to a route, it is a refusal.
 */
function satisfies(
  granted: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some((alternative) => alternative.every((scope) => granted.has(scope)));
}

export function resolvePage(page: PageDefinition, granted: ReadonlySet<string>): ResolvedPage {
  // A page the platform cannot yet support is forbidden to everyone, including
  // an operator holding every scope in existence. Showing it and failing would
  // teach an operator that the console is unreliable; the reason lives on the
  // definition so it can be reported rather than discovered.
  if (page.blockedBy !== undefined) return { page, access: "forbidden", visible: false };

  const canRead = satisfies(granted, page.readRequirements);
  const canWrite =
    page.writeRequirements !== undefined && satisfies(granted, page.writeRequirements);

  // Write without read is not a contradiction to paper over. An operator who
  // may resolve an exception but may not list exceptions can still act on one
  // they were sent directly, so the page opens — but it cannot be browsed, and
  // navigation must not imply that it can.
  if (!canRead && canWrite) return { page, access: "full", visible: false };
  if (!canRead) return { page, access: "forbidden", visible: false };
  return { page, access: canWrite ? "full" : "read_only", visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/**
 * Navigation for one operator, grouped, with empty sections dropped.
 *
 * A section heading with nothing under it is a promise the product does not
 * keep, and on a console with nine pages it is most of the sidebar for a
 * narrowly-scoped operator.
 */
export function buildNavigation(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = dispatchConsolePages,
): NavigationSection[] {
  const sections: NavigationSection[] = [];
  for (const page of pages) {
    const resolved = resolvePage(page, granted);
    if (!resolved.visible) continue;
    const existing = sections.find((candidate) => candidate.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else (existing.pages as ResolvedPage[]).push(resolved);
  }
  return sections;
}

/**
 * Whether an operator can open a page they navigated to directly.
 *
 * Separate from visibility on purpose. A link pasted into a chat, a bookmark
 * from a previous role, a deep link in an alert — all arrive without passing
 * through navigation, and answering them from the same rule that hides a menu
 * item is how a console ends up showing a blank screen instead of a reason.
 */
export function canOpen(pageId: string, granted: ReadonlySet<string>): boolean {
  const page = dispatchConsolePages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, granted).access !== "forbidden";
}

/** Every scope the console can make use of, for a token request or an audit. */
export function allConsoleScopes(
  pages: readonly PageDefinition[] = dispatchConsolePages,
): string[] {
  const scopes = new Set<string>();
  for (const page of pages) {
    if (page.blockedBy !== undefined) continue;
    for (const scope of page.readRequirements.flat()) scopes.add(scope);
    for (const scope of (page.writeRequirements ?? []).flat()) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * How many pages this operator cannot see, and what each would need.
 *
 * The rail's foot says so — `Console Shell/01 Shell light.jpg` reads "1 page not in your role" —
 * and it is the difference between a console that looks small and a console that looks broken. An
 * operator who can see two of ten pages is told there are eight more and what they cost, rather than
 * left to wonder whether the product is missing or their account is.
 *
 * Only pages hidden by scope. A page the platform cannot support yet is invisible to everybody
 * including an operator holding every scope, so counting it here would tell an operator their role
 * is short of something no role has.
 */
export function pagesNotInRole(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = dispatchConsolePages,
): { id: string; title: string; needs: readonly string[] }[] {
  return pages
    .filter((page) => page.blockedBy === undefined && !resolvePage(page, granted).visible)
    .map((page) => ({
      id: page.id,
      title: page.title,
      // The cheapest way in: the alternative with fewest scopes the operator does not already hold.
      needs:
        [...page.readRequirements]
          .map((alternative) => alternative.filter((scope) => !granted.has(scope)))
          .sort((left, right) => left.length - right.length)[0] ?? [],
    }));
}

/** The pages the platform cannot support yet, and why. */
export function blockedPages(
  pages: readonly PageDefinition[] = dispatchConsolePages,
): { id: string; blockedBy: string }[] {
  return pages
    .filter((page): page is PageDefinition & { blockedBy: string } => page.blockedBy !== undefined)
    .map((page) => ({ id: page.id, blockedBy: page.blockedBy }));
}
